import { useEffect, useState } from "react";
import { generateClient } from "aws-amplify/api";
import type { GraphQLResult } from "aws-amplify/api";
import { GET_JOB, LIST_JOBS } from "./graphql";
import type { GetJobResult, JobDetail, JobSummary, ListJobsResult } from "./graphql";

const RESUMING_MESSAGE = "The job store is waking up, retry in a few seconds";

type Client = ReturnType<typeof generateClient>;

async function runQuery<T>(client: Client, query: string, variables: Record<string, string | number>): Promise<T> {
  const response = (await client.graphql<unknown>({ query, variables })) as GraphQLResult<T>;
  if (response.errors?.length) {
    throw response;
  }
  return response.data;
}

// AppSync errors arrive as a GraphQLResult with errors[].errorType (thrown by
// Amplify or by runQuery); anything else is a plain Error.
function errorMessage(error: unknown): string {
  const errors = (error as { errors?: { message?: string; errorType?: string }[] })?.errors;
  if (Array.isArray(errors) && errors.length > 0) {
    if (errors.some((e) => e.errorType === "DatabaseResuming")) {
      return RESUMING_MESSAGE;
    }
    return errors.map((e) => e.message ?? "Unknown error").join("; ");
  }
  return error instanceof Error ? error.message : "Unknown error";
}

function JobDetailView({ job }: { job: JobDetail }) {
  return (
    <section className="status-card" aria-label="Job detail">
      <h2>{job.job_id}</h2>
      <dl>
        <dt>Status</dt>
        <dd>{job.status}</dd>
        <dt>Owner</dt>
        <dd>{job.owner}</dd>
        <dt>Weblab</dt>
        <dd>{job.weblab}</dd>
        <dt>Marketplace</dt>
        <dd>{job.marketplace}</dd>
        <dt>Data source</dt>
        <dd>{job.data_source}</dd>
        <dt>Trigger source</dt>
        <dd>{job.weblab_trigger_source}</dd>
        <dt>Treatments</dt>
        <dd>{job.treatments}</dd>
        <dt>Cohorts</dt>
        <dd>{job.cohorts}</dd>
        <dt>Metrics</dt>
        <dd>{job.metrics}</dd>
        <dt>Dates</dt>
        <dd>
          {job.start_date} to {job.end_date}
        </dd>
        <dt>Created</dt>
        <dd>{job.created_at ?? "-"}</dd>
        <dt>Started</dt>
        <dd>{job.started_at ?? "-"}</dd>
        <dt>Ended</dt>
        <dd>{job.ended_at ?? "-"}</dd>
      </dl>
      {job.failure && (
        <div role="alert">
          <h3>Failure</h3>
          <p>
            {[job.failure.stage, job.failure.errorType, job.failure.table, job.failure.date]
              .filter(Boolean)
              .join(" / ")}
          </p>
          {job.failure.message && <p>{job.failure.message}</p>}
        </div>
      )}
      {job.resultFiles && job.resultFiles.length > 0 && (
        <div>
          <h3>Results</h3>
          <ul>
            {job.resultFiles.map((file) => (
              <li key={file.key}>
                {file.url.startsWith("https://") ? (
                  <a href={file.url} target="_blank" rel="noopener noreferrer">
                    {file.key}
                  </a>
                ) : (
                  <span>{file.key}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function Jobs() {
  const [client] = useState<Client>(() => generateClient());
  const [jobs, setJobs] = useState<JobSummary[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [selected, setSelected] = useState<JobDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  useEffect(() => {
    runQuery<ListJobsResult>(client, LIST_JOBS, { limit: 25 })
      .then((data) => setJobs(data.listJobs.items))
      .catch((error) => setListError(errorMessage(error)));
  }, [client]);

  const openJob = (jobId: string) => {
    setDetailLoading(true);
    setDetailError(null);
    setSelected(null);
    runQuery<GetJobResult>(client, GET_JOB, { job_id: jobId })
      .then((data) => {
        if (data.getJob) {
          setSelected(data.getJob);
        } else {
          setDetailError(`Job ${jobId} was not found`);
        }
      })
      .catch((error) => setDetailError(errorMessage(error)))
      .finally(() => setDetailLoading(false));
  };

  return (
    <>
      <section className="status-card">
        <h2>Jobs</h2>
        {listError && <p role="alert">{listError}</p>}
        {!listError && jobs === null && <p>Loading jobs...</p>}
        {jobs !== null && jobs.length === 0 && <p>No jobs yet.</p>}
        {jobs !== null && jobs.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Job id</th>
                <th>Status</th>
                <th>Weblab</th>
                <th>Marketplace</th>
                <th>Owner</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <tr key={job.job_id} onClick={() => openJob(job.job_id)} style={{ cursor: "pointer" }}>
                  <td>{job.job_id}</td>
                  <td>{job.status}</td>
                  <td>{job.weblab}</td>
                  <td>{job.marketplace}</td>
                  <td>{job.owner}</td>
                  <td>{job.created_at ?? "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      {detailLoading && <p>Loading job...</p>}
      {detailError && <p role="alert">{detailError}</p>}
      {selected && <JobDetailView job={selected} />}
    </>
  );
}

export default Jobs;
